import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const validate = (form) => {
  const errors = {};

  // Name
  if (!form.name.trim()) {
    errors.name = 'Full name is required.';
  } else if (form.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters.';
  } else if (!/^[a-zA-Z\s]+$/.test(form.name.trim())) {
    errors.name = 'Name can only contain letters.';
  }

  // Mobile
  if (!form.mobile.trim()) {
    errors.mobile = 'Mobile number is required.';
  } else if (!/^[6-9]\d{9}$/.test(form.mobile.replace(/\s+/g, ''))) {
    errors.mobile = 'Enter a valid 10-digit Indian mobile number.';
  }

  // Email
  if (!form.email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'Enter a valid email address.';
  }

  // Address
  if (!form.address.trim()) {
    errors.address = 'Address is required.';
  } else if (form.address.trim().length < 10) {
    errors.address = 'Please enter your full address (min 10 characters).';
  }

  // Password
  if (!form.password) {
    errors.password = 'Password is required.';
  } else if (form.password.length < 6) {
    errors.password = 'Password must be at least 6 characters.';
  } else if (!/[A-Z]/.test(form.password)) {
    errors.password = 'Password must contain at least one uppercase letter.';
  } else if (!/[0-9]/.test(form.password)) {
    errors.password = 'Password must contain at least one number.';
  }

  // Confirm password
  if (!form.confirm) {
    errors.confirm = 'Please confirm your password.';
  } else if (form.password !== form.confirm) {
    errors.confirm = 'Passwords do not match.';
  }

  return errors;
};

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate     = useNavigate();
  const [form,    setForm]    = useState({ name: '', email: '', mobile: '', address: '', password: '', confirm: '' });
  const [errors,  setErrors]  = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPass,setShowPass]= useState(false);

  const handle = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    // Re-validate this field live once it's been touched
    if (touched[name]) {
      const newErrors = validate({ ...form, [name]: value });
      setErrors(prev => ({ ...prev, [name]: newErrors[name] }));
    }
  };

  const handleBlur = e => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const newErrors = validate(form);
    setErrors(prev => ({ ...prev, [name]: newErrors[name] }));
  };

  const submit = async e => {
    e.preventDefault();
    // Mark all fields as touched and run full validation
    const allTouched = Object.keys(form).reduce((acc, k) => ({ ...acc, [k]: true }), {});
    setTouched(allTouched);
    const validationErrors = validate(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      toast.error('Please fix the errors before submitting.');
      return;
    }
    setLoading(true);
    try {
      const user = await register({ name: form.name, email: form.email, mobile: form.mobile, address: form.address, password: form.password });
      toast.success(`Welcome, ${user.name}! Account created.`);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed.');
    } finally { setLoading(false); }
  };

  const field = (name) => ({
    name,
    value: form[name],
    onChange: handle,
    onBlur: handleBlur,
    className: `input-field ${touched[name] && errors[name] ? 'border-error ring-1 ring-error' : ''}`,
  });

  const ErrorMsg = ({ name }) =>
    touched[name] && errors[name]
      ? <p className="font-label text-xs text-error mt-1">{errors[name]}</p>
      : null;

  return (
    <div className="min-h-screen flex bg-surface">
      {/* Left visual */}
      <div className="hidden lg:flex lg:w-1/2 bg-on-surface items-center justify-center relative overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-5">
          <defs>
            <pattern id="bp2" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <circle cx="30" cy="30" r="10" fill="none" stroke="white" strokeWidth="1.5" />
              <circle cx="30" cy="30" r="4" fill="white" />
              <line x1="30" y1="20" x2="30" y2="14" stroke="white" strokeWidth="1.5" />
              <line x1="30" y1="40" x2="30" y2="46" stroke="white" strokeWidth="1.5" />
              <line x1="20" y1="30" x2="14" y2="30" stroke="white" strokeWidth="1.5" />
              <line x1="40" y1="30" x2="46" y2="30" stroke="white" strokeWidth="1.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#bp2)" />
        </svg>
        <div className="absolute left-0 top-0 w-2 h-full bg-primary-container" />
        <div className="relative z-10 text-center px-12">
          <h1 className="font-headline font-black text-5xl uppercase tracking-tighter text-white leading-none">
            Join Us
          </h1>
          <p className="font-label text-xs uppercase tracking-[0.3em] text-gray-500 mt-3">Create your account</p>
          <div className="mt-10 space-y-4 text-left max-w-xs mx-auto">
            {['Browse our full product catalogue', 'Submit inquiries easily', 'Track your inquiry history', 'Receive PDF confirmations'].map(b => (
              <div key={b} className="flex items-center gap-3">
                <div className="w-5 h-5 bg-primary-container flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-white text-[12px]">check</span>
                </div>
                <span className="font-body text-sm text-gray-400">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <Link to="/">
              <span className="font-headline font-black text-3xl uppercase tracking-tighter text-on-surface">
                Central <span className="text-primary-container">Fasteners</span>
              </span>
            </Link>
          </div>

          <div className="mb-8">
            <h2 className="font-headline font-black text-3xl uppercase tracking-tight">Create Account</h2>
            <p className="font-body text-sm text-secondary mt-1">Fill in your details to get started.</p>
          </div>

          <form onSubmit={submit} noValidate className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="input-label">Full Name *</label>
                <input {...field('name')} placeholder="Your full name" />
                <ErrorMsg name="name" />
              </div>
              <div>
                <label className="input-label">Mobile Number *</label>
                <input {...field('mobile')} placeholder="10-digit mobile number" maxLength={10} />
                <ErrorMsg name="mobile" />
              </div>
            </div>

            <div>
              <label className="input-label">Email Address *</label>
              <input {...field('email')} type="email" placeholder="you@example.com" />
              <ErrorMsg name="email" />
            </div>

            <div>
              <label className="input-label">Address *</label>
              <textarea {...field('address')} rows={2} className={`input-field resize-none ${touched.address && errors.address ? 'border-error ring-1 ring-error' : ''}`} placeholder="Your full address" />
              <ErrorMsg name="address" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="input-label">Password *</label>
                <div className="relative">
                  <input {...field('password')} type={showPass ? 'text' : 'password'} placeholder="Min 6 chars, 1 uppercase, 1 number"
                    className={`input-field pr-10 ${touched.password && errors.password ? 'border-error ring-1 ring-error' : ''}`} />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary-container">
                    <span className="material-symbols-outlined text-[18px]">{showPass ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
                <ErrorMsg name="password" />
              </div>
              <div>
                <label className="input-label">Confirm Password *</label>
                <input {...field('confirm')} type="password" placeholder="Repeat password" />
                <ErrorMsg name="confirm" />
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-4 text-base disabled:opacity-70">
              {loading
                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Creating...</>
                : <><span className="material-symbols-outlined">person_add</span> Create Account</>
              }
            </button>
          </form>

          <p className="font-body text-sm text-secondary text-center mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-container font-bold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
